// ** React Imports
import { useCallback, useContext, useRef, useState, useEffect } from "react";

// ** Third Party Components
import ReactPlayer from "react-player";

//** Package
import { toast } from "helpers/toastWrapper";

// ** Custom Components
import Block from "components/layout/Block";
import Text from "components/typography/Text";
import BottomPanel from "./BottomPanel";
import VolumeBar from "./VolumeBar";
import DynamicSlashVideoIcon from "./DynamicSlashVideoIcon";
import DynamicVolumeIcon from "components/icons/DynamicVolumeIcon";
import DynamicSlashMicrophoneIcon from "components/icons/DynamicSlashMicrophoneIcon";
import GearIcon from "components/icons/GearIcon";
import DynamicFullscreenIcon from "components/icons/DynamicFullscreenIcon";
import { OuterCameraPublishContainer } from "../styles";
import Webcam from "react-webcam";
import PublisherEmptyStreamLayout from "./PublisherEmptyStreamLayout";

// ** Hooks
import useMobileDetect from "hooks/useMobileDetectHook";
import useWatcherCameraPlayer from "../hooks/useWatcherCameraPlayer";
import useOnClickOutside from "hooks/useOnClickOutside";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";
import { useTranslation } from "react-i18next";

// ** Styled Components
import {
	ControlButton,
	ControlButtonVolume,
	ControlButtonContainer,
	StyledWaitingToStartTransmissionMessage,
} from "../styles";
import { Dropdown, SwitchingCameraButton } from "../styles/styles";

// ##################################

const WatcherCameraCloudflare = ({
	watcherMediaStream,
	watcherHash,
	isPublisherConnected,
	publisherMuteStream,
	publisherUnmuteStream,
	publisherCameraRef,
	chatPartnerCameraAndMic,
	chatPartnerStreamStatus,
	showYou,
	camOff,
	setCamOff,
	handleClickShowYou,
	reactPlayerRef,
	selectedCamera,
	setSelectedCamera,
	showingInProgress,
	videoChatRef,
	loggedUser,
	setLocalStream,
	setShowYou,
}) => {
	const { t } = useTranslation(["userpanel"]);
	const { isPhone } = useContext(deviceTypeContext);
	const mobileDetect = useMobileDetect();
	const isIos = mobileDetect.isIos();

	const switchingCameraSettingsRef = useRef(null);

	const [showCamSettings, setShowCamSettings] = useState(false);
	const [publisherStreamMuted, setPublisherStreamMuted] = useState(false);
	const [videoDevices, setVideoDevices] = useState([]);

	const {
		muted,
		toggleMuted,
		volume,
		changeVolume,
		fullscreen,
		toggleFullscreen,
	} = useWatcherCameraPlayer({
		reactPlayerRef,
		playerWrapperRef: videoChatRef,
	});

	if (
		"mediaDevices" in navigator &&
		"enumerateDevices" in navigator.mediaDevices
	) {
		navigator.mediaDevices.enumerateDevices().then((devices) => {
			const videoDevices = devices.filter(
				(device) => device.kind === "videoinput"
			);
			setVideoDevices(videoDevices);
		});
	}

	const renderControlCameraButton = useCallback(
		() => (
			<ControlButton
				onClick={() => handleClickShowYou()}
				disabled={showYou && !isPublisherConnected}
				customMargin="0 0 0.5rem 0"
			>
				<DynamicSlashVideoIcon
					customHeight="17px"
					slash={!showYou || camOff}
				/>
			</ControlButton>
		),
		[showYou, isPublisherConnected, camOff, handleClickShowYou]
	);

	const renderControlMuteStreamButton = useCallback(() => {
		return (
			<ControlButton
				onClick={() => {
					if (publisherStreamMuted) {
						publisherUnmuteStream();
						setPublisherStreamMuted(false);
					} else if (!publisherStreamMuted) {
						publisherMuteStream();
						setPublisherStreamMuted(true);
					}
				}}
				disabled={!isPublisherConnected || !showYou}
				customMargin="0 0 0.5rem 0"
			>
				<DynamicSlashMicrophoneIcon
					customHeight="17px"
					slash={publisherStreamMuted || !showYou}
				/>
			</ControlButton>
		);
	}, [
		publisherStreamMuted,
		isPublisherConnected,
		showYou,
		publisherMuteStream,
		publisherUnmuteStream,
	]);

	useOnClickOutside(switchingCameraSettingsRef, (e) => {
		if (e.offsetX < e.target.clientWidth) {
			// ** Warunek sprawdzający czy kliknięto prawym przyciskiem myszy - jeśli tak to nie zamyka modala
			if (e.which !== 3) {
				setShowCamSettings(false);
			}
		}
	});

	const renderCameraSwitchingButton = useCallback(() => {
		return (
			<ControlButton
				onClick={() => {
					setShowCamSettings(!showCamSettings);
				}}
				customMargin="0 0 0.5rem 0"
				disabled={!isPublisherConnected || showingInProgress.current}
			>
				<GearIcon customHeight="17px" />
			</ControlButton>
		);
	}, [publisherCameraRef.current, showCamSettings, isPublisherConnected]);

	const renderControlVolumeBar = useCallback(
		() => (
			<VolumeBar
				volume={muted ? 0 : volume}
				onChange={(e) => changeVolume(e.target.value)}
			/>
		),
		[volume, muted]
	);

	const renderControlVolumeMuteButton = useCallback(() => {
		return (
			<ControlButtonVolume onClick={() => toggleMuted()}>
				<DynamicVolumeIcon
					customHeight="17px"
					volume={muted ? 0 : volume}
				/>
			</ControlButtonVolume>
		);
	}, [volume, muted]);

	const renderControlVolume = useCallback(
		() => (
			<ControlButtonContainer
				style={!isPhone ? { width: "100px", padding: "0 0.6rem" } : {}}
			>
				{renderControlVolumeMuteButton()}
				{!isPhone && renderControlVolumeBar()}
			</ControlButtonContainer>
		),
		[volume, muted]
	);

	const renderControlFullscreenButton = useCallback(
		() => (
			<ControlButton
				onClick={toggleFullscreen}
				customMargin="0 0 0.5rem 0"
			>
				<DynamicFullscreenIcon
					customHeight="17px"
					fullscreen={fullscreen}
				/>
			</ControlButton>
		),
		[fullscreen, toggleFullscreen]
	);

	useEffect(() => {
		if (showYou === false) {
			setPublisherStreamMuted(true);
			publisherMuteStream();
		} else if (showYou) {
			setPublisherStreamMuted(false);
			publisherUnmuteStream();
		}
	}, [showYou]);

	const handleSwitchCamera = (mode) => {
		if (selectedCamera !== mode) {
			setSelectedCamera(mode);
			setPublisherStreamMuted(false);
		}
		if (camOff) {
			setCamOff(false);
		}
		setShowCamSettings(false);
	};

	return (
		<>
			<Block
				flex
				column
				justifyCenter
				style={{
					width: fullscreen ? "100vw" : isPhone ? "100%" : "400px",
					height: fullscreen ? "100vh" : "100%",
				}}
			>
				{showCamSettings && (
					<Dropdown flex column ref={switchingCameraSettingsRef}>
						<SwitchingCameraButton
							key="user"
							onClick={() => handleSwitchCamera("user")}
							style={{
								fontWeight:
									selectedCamera === "user" ? "bold" : null,
							}}
						>
							<Text
								style={{
									display: "inline-block",
									maxWidth: "200px",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
								color="whiteAlways"
							>
								Facing Camera
							</Text>
						</SwitchingCameraButton>
						<SwitchingCameraButton
							key="environment"
							onClick={() => handleSwitchCamera("environment")}
							style={{
								fontWeight:
									selectedCamera === "environment"
										? "bold"
										: null,
							}}
						>
							<Text
								style={{
									display: "inline-block",
									maxWidth: "200px",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
								color="whiteAlways"
							>
								Facing-Out Camera
							</Text>
						</SwitchingCameraButton>
					</Dropdown>
				)}
				<div style={{ overflow: "hidden" }}>
					{watcherHash &&
					chatPartnerCameraAndMic !== "off" &&
					chatPartnerStreamStatus !== "stream_off" ? (
						<ReactPlayer
							ref={reactPlayerRef}
							url={watcherMediaStream}
							style={{
								display: isPhone ? "flex" : null,
								alignItems: isPhone ? "center" : null,
								justifyContent: isPhone ? "center" : null,
							}}
							width="100%"
							height={fullscreen ? "100%" : "325px"}
							playing={true}
							muted={muted}
							controls={isIos}
							playsinline={true}
						/>
					) : (
						<div
							style={
								fullscreen
									? {
											position: "absolute",
											top: 0,
											width: "100%",
											maxWidth: "75vw",
									  }
									: {
											position: "relative",
											width: "100%",
											height: "325px",
									  }
							}
						>
							<StyledWaitingToStartTransmissionMessage>
								{t(
									"userpanel:messages.waitingToStartTransmissionByFriend"
								)}
							</StyledWaitingToStartTransmissionMessage>
						</div>
					)}
				</div>
				<BottomPanel
					controlCameraButton={renderControlCameraButton}
					controlMuteStreamButton={renderControlMuteStreamButton}
					cameraSwitchingButton={
						videoDevices.length > 1
							? renderCameraSwitchingButton
							: null
					}
					controlFullscreenButton={
						isIos ? null : renderControlFullscreenButton
					}
					controlVolume={renderControlVolume}
					fullscreen={isIos ? null : fullscreen}
				/>
			</Block>
			<OuterCameraPublishContainer
				onClick={handleClickShowYou}
				style={{ cursor: "pointer" }}
				$fullscreen={fullscreen}
			>
				{showYou ? (
					<Webcam
						ref={publisherCameraRef}
						screenshotFormat="image/jpeg"
						videoConstraints={{
							facingMode: selectedCamera,
						}}
						height="100%"
						audio
						muted
						onUserMedia={(stream) => {
							setLocalStream(stream);
						}}
						onUserMediaError={(err) => {
							showingInProgress.current = false;
							setShowYou(false);
							setLocalStream(null);
							toast.error(t("userpanel:camera.unavailable"));
						}}
					/>
				) : (
					<PublisherEmptyStreamLayout loggedUser={loggedUser} />
				)}
				{camOff && (
					<div
						style={{
							position: "absolute",
							top: 0,
							right: 0,
							left: 0,
							bottom: 0,
						}}
					>
						<PublisherEmptyStreamLayout loggedUser={loggedUser} />
					</div>
				)}
			</OuterCameraPublishContainer>
		</>
	);
};

export default WatcherCameraCloudflare;
